import React, { useCallback, useEffect, useRef } from "react";
import { useTranscriptState } from "./useTranscriptState";
import { useStageAnchorCentering } from "../useStageAnchorCentering";
import MobileTranscriptDrawer from "./MobileTranscriptDrawer";
import { TranscriptMessage } from "../components/TranscriptPanel";
import type { ChatAppearanceProps } from "@/types/voice/chat-appearance";

interface MobileLiveAgentLayoutProps {
  chatAppearance?: ChatAppearanceProps;
  children: React.ReactNode;
  brandColor: string;
  secondaryColor?: string | null;
  agentName?: string;
  messages: TranscriptMessage[];
  rightInlineIcons?: React.ReactNode;
  /**
   * The resources rail, laid down as a horizontal band. Docked at the bottom
   * of the screen while the chat drawer is closed, so the action row stays
   * directly under the genie. With the drawer open, the suggested resources
   * are cards in the chat instead (see useSuggestedResourceMessages).
   */
  resourcesRail?: React.ReactNode;
  /**
   * Voice controls (mic mute / volume / pause). Rendered inline at the bottom
   * of the avatar column when the chat drawer is closed, AND mirrored into the
   * drawer header's left slot when the drawer is open — so the user always has
   * mic/volume access whether they're looking at the avatar or the chat.
   */
  drawerHeaderVoiceControls?: React.ReactNode;
  /** End the active voice conversation from the chat header. */
  onEndConversation?: () => void;
  isConnected?: boolean;
  isListening?: boolean;
  isConnecting?: boolean;
  isSpeaking?: boolean;
  onSendMessage?: (text: string) => void;
  isAllAudioMuted?: boolean;
  onToggleAllAudioMute?: () => void;
  forceTranscriptOpen?: boolean; // Force transcript drawer open (e.g., for text-only mode)
  suppressTranscript?: boolean;
  // Text-only mode props
  isTextOnlyMode?: boolean;
  inlineMessage?: string;
  onChangeInlineMessage?: (value: string) => void;
  onSendChat?: () => void;
  startedFromText?: boolean;
  onEndChat?: () => void;
  attachments?: Array<{
    id: string;
    previewUrl: string;
    status: "analyzing" | "ready" | "error";
    analysisText?: string;
  }>;
  onRemoveAttachment?: (id: string) => void;
  onOpenFilePicker?: () => void;
  onUpdateAttachmentAnalysis?: (id: string, text: string) => void;
  imageUploadPendingCount?: number;
  imageUploadFailedCount?: number;
  imageUploadSaving?: boolean;
  onRetryImageUploads?: () => void;
  onDrawerOpenChange?: (open: boolean) => void;
  /** The in-conversation capture moment, forwarded to the chat drawer's message stream. */
  inlineCaptureSlot?: React.ReactNode;
  /**
   * Rendered beneath the action row when the drawer is closed: what the Genie
   * shared on screen. Keeps the controls directly under the genie however much
   * is shared.
   */
  belowActionRow?: React.ReactNode;
}

// The pages pin their "Having trouble?" pill to the very bottom of the screen
// during a voice conversation; the docked rail sits clear above it.
const RAIL_BOTTOM_CLEARANCE = "calc(56px + env(safe-area-inset-bottom))";

export const MobileLiveAgentLayout = ({
  chatAppearance,
  children,
  brandColor,
  secondaryColor,
  agentName,
  messages,
  rightInlineIcons,
  resourcesRail,
  belowActionRow,
  drawerHeaderVoiceControls,
  onEndConversation,
  isConnected = false,
  isListening = false,
  isConnecting = false,
  isSpeaking = false,
  onSendMessage,
  isAllAudioMuted = false,
  onToggleAllAudioMute,
  forceTranscriptOpen = false,
  suppressTranscript = false,
  isTextOnlyMode = false,
  inlineMessage = "",
  onChangeInlineMessage,
  onSendChat,
  startedFromText = false,
  onEndChat,
  attachments = [],
  onRemoveAttachment,
  onOpenFilePicker,
  onUpdateAttachmentAnalysis,
  imageUploadPendingCount,
  imageUploadFailedCount,
  imageUploadSaving,
  onRetryImageUploads,
  onDrawerOpenChange,
  inlineCaptureSlot,
}: MobileLiveAgentLayoutProps) => {
  const { open, setOpen: setOpenInternal } = useTranscriptState();

  const setOpen = useCallback((v: boolean) => {
    setOpenInternal(v);
    onDrawerOpenChange?.(v);
  }, [setOpenInternal, onDrawerOpenChange]);

  // Sync initial open state to parent
  useEffect(() => {
    if (open) onDrawerOpenChange?.(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Force transcript open only for true text-start conversations.
  useEffect(() => {
    if (suppressTranscript) {
      if (open) setOpen(false);
      return;
    }
    if ((forceTranscriptOpen || startedFromText) && !open) {
      setOpen(true);
    }
  }, [forceTranscriptOpen, suppressTranscript, startedFromText, open, setOpen]);

  // Keep base page geometry stable; drawer opens as an overlay.
  const topPadding = 24;
  const bottomPadding = 24;
  const maxContentWidth = 620;

  // Hold the genie at the centre of the screen however tall the status text,
  // the controls or the docked rail are.
  const scrollerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const anchorOffset = useStageAnchorCentering(scrollerRef, stageRef, !open);

  return (
    <div
      style={{
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
      }}
    >
      {/* The stage scrolls inside its own box, so content that grows taller
          than the screen (several shared pictures under the genie) never
          spills up under the page's header buttons. */}
      <div
        ref={scrollerRef}
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          width: "100%",
          overflowY: "auto",
          overscrollBehavior: "contain",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          ref={stageRef}
          className="motion-safe:transition-[top] motion-safe:duration-300 motion-safe:ease-out"
          style={{
            position: "relative",
            top: anchorOffset,
            maxWidth: maxContentWidth,
            // Auto margins centre the stage while it fits and fall back to
            // the top once it doesn't, where centring would clip its start.
            margin: "auto",
            paddingTop: topPadding,
            paddingBottom: bottomPadding,
            paddingLeft: "max(12px, env(safe-area-inset-left))",
            paddingRight: "max(12px, env(safe-area-inset-right))",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 12,
            width: "100%",
            boxSizing: "border-box",
            textAlign: "center",
          }}
        >
          {children}
          {!open && rightInlineIcons ? <div style={{ marginTop: 12 }}>{rightInlineIcons}</div> : null}
          {!open && belowActionRow ? <div style={{ marginTop: 16, width: "100%" }}>{belowActionRow}</div> : null}
        </div>
      </div>

      {!open && resourcesRail ? (
        <div
          style={{
            width: "100%",
            maxWidth: maxContentWidth,
            boxSizing: "border-box",
            paddingLeft: "max(12px, env(safe-area-inset-left))",
            paddingRight: "max(12px, env(safe-area-inset-right))",
            paddingBottom: RAIL_BOTTOM_CLEARANCE,
            textAlign: "left",
          }}
        >
          {resourcesRail}
        </div>
      ) : null}

      {/* Removed bottom floating transcript button per request */}

      <MobileTranscriptDrawer
        chatAppearance={chatAppearance}
        open={open && !suppressTranscript}
        onOpenChange={setOpen}
        brandColor={brandColor}
        secondaryColor={secondaryColor}
        agentName={agentName}
        messages={messages}
        isConnected={isConnected}
        isListening={isListening}
        isConnecting={isConnecting}
        isSpeaking={isSpeaking}
        onSendMessage={onSendMessage}
        isAllAudioMuted={isAllAudioMuted}
        onToggleAllAudioMute={onToggleAllAudioMute}
        isTextOnlyMode={isTextOnlyMode}
        inlineMessage={inlineMessage}
        onChangeInlineMessage={onChangeInlineMessage}
        onSend={onSendChat}
        startedFromText={startedFromText}
        onEndChat={onEndChat}
        attachments={attachments}
        onRemoveAttachment={onRemoveAttachment}
        onOpenFilePicker={onOpenFilePicker}
        onUpdateAttachmentAnalysis={onUpdateAttachmentAnalysis}
        imageUploadPendingCount={imageUploadPendingCount}
        imageUploadFailedCount={imageUploadFailedCount}
        imageUploadSaving={imageUploadSaving}
        onRetryImageUploads={onRetryImageUploads}
        headerVoiceControls={drawerHeaderVoiceControls}
        onEndConversation={onEndConversation}
        isLockedOpen={startedFromText}
        inlineCaptureSlot={inlineCaptureSlot}
      />
    </div>
  );
};

export default MobileLiveAgentLayout;


